import { IkeDelayedTask } from '@ikegps/core-shared';
import { action, computed, observable, makeObservable } from 'mobx';
import { m, type MChild } from '../../mithril';
import type { MithrilVNode } from '../../mithril';
import { Services } from '../../services';
import { InputEventBindContext } from '../../services/input/event.context';
import type { EventData } from '../../services/input/input.service';
import { IMithrilStateComponent } from '../mithril.component';
import { addStyles } from '../theme';
import { type MenuItem, MenuItemWrapperComponent } from './menu.item';
import { CSS, Style } from './menu.style';

addStyles(Style);

export interface MenuComponentAttrs {
	open?: () => boolean;
	className?: string;
	items: () => MenuItem[];
}

export class MenuComponent extends IMithrilStateComponent {
	className: string;
	itemsFunc: () => MenuItem[];

	closeTask = new IkeDelayedTask(() => this.remove(), 200);

	@observable isOpen = false;
	@observable isClosing = false;
	@observable selectedItemIndex: number = 0;

	openFunc: () => boolean;

	eventContext: InputEventBindContext;

	constructor(attrs: MenuComponentAttrs) {
		super();

		makeObservable(this);

		this.className = attrs.className;
		this.itemsFunc = attrs.items;
		this.openFunc = attrs.open;
		this.eventContext = Services.Input.getContext();
	}

	override onCreate() {
		if (typeof this.openFunc === 'function') {
			this.reaction(() => this.openFunc(), this.toggle, { fireImmediately: true });
		}
	}

	get nodes(): MithrilVNode {
		if (!this.isOpen) {
			return null;
		}

		let className = `${CSS.menu} ${this.isOpen ? CSS.isOpen : CSS.isClosed}`;
		if (this.className != null) {
			className += ` ${this.className}`;
		}
		if (this.isClosing) {
			className += ` ${CSS.isClosing}`;
		}

		const items = this.items;
		if (items == null || items.length === 0) {
			return null;
		}

		const children: MChild[] = [];
		for (let i = 0; i < items.length; i++) {
			const menuItem = items[i];

			if (menuItem.component) {
				children.push(menuItem.view);
			} else {
				const selected = i === this.selectedItemIndex;
				children.push(MenuItemWrapperComponent.view(this, menuItem, selected));
			}

			if (menuItem.divider) {
				children.push(m('div', { className: CSS.divider }));
			}
		}

		return m(
			'div',
			{
				className,
				oncreate: (node: MithrilVNode) => Services.FocusAway.from(node.dom, this.hide),
			},
			children
		);
	}

	@computed
	private get items(): MenuItem[] {
		const menuItems = this.itemsFunc();
		if (menuItems == null) {
			return [];
		}

		return menuItems;
	}

	@computed get hasUsableItems(): boolean {
		const items = this.items;
		for (const item of items) {
			if (item.visible !== false && !item.disabled) {
				return true;
			}
		}

		return false;
	}

	@action.bound remove() {
		this.isOpen = false;
		this.isClosing = false;
	}

	@action.bound hide() {
		this.toggle(false);
	}

	@action.bound show() {
		this.toggle(true);
	}

	@action.bound toggle(shouldOpen: boolean) {
		if (shouldOpen) {
			this.isOpen = true;
			this.isClosing = false;
			this.closeTask.cancel();
			this.bindKeyboardEvents();

			const selectedIndex = this.items.findIndex((i) => i.selected);
			this.selectedItemIndex = Math.max(0, selectedIndex);
		} else {
			this.isClosing = true;
			if (this.closeTask.isActive) {
				return;
			}
			this.closeTask.delay();
			this.eventContext.destroy();
		}
	}

	@action selectItem(item: MenuItem) {
		const selectedIndex = this.items.findIndex((i) => i === item);
		this.selectedItemIndex = Math.max(0, selectedIndex);
	}

	@action.bound
	private selectCurrentItem(e: EventData) {
		e.event.stopPropagation();
		e.event.preventDefault();

		const menuItem = this.getSelectedItem();
		if (menuItem != null && menuItem.action != null) {
			menuItem.action();
		}

		this.hide();
	}

	private getSelectedItem(): MenuItem | null {
		return this.items[this.selectedItemIndex];
	}

	@action
	private selectItemIndex(delta: number) {
		const items = this.items;

		let index = this.wrap(this.selectedItemIndex + delta, items.length);
		while (items[index].visible === false || items[index].disabled) {
			if (index === this.selectedItemIndex) {
				// No other selectable items. Stop
				return;
			}

			index = this.wrap(index + delta, items.length);
		}

		this.selectedItemIndex = index;
	}

	private wrap(index: number, count: number): number {
		return ((index % count) + count) % count;
	}

	private bindKeyboardEvents() {
		this.eventContext.on(this.eventContext.Code.ArrowUp, () => this.selectItemIndex(-1));
		this.eventContext.on(this.eventContext.Code.ArrowDown, () => this.selectItemIndex(1));
		this.eventContext.on(this.eventContext.Code.Enter, this.selectCurrentItem);
		this.eventContext.on(this.eventContext.Code.Escape, this.hide);
	}

	override onRemove() {
		super.onRemove();
		this.eventContext.destroy();
	}
}
